import { useMemo } from "react";
import type { PaywallPrice } from "@circle-react/api/paywallCheckoutApi.types";
import type { UsePaywallCheckoutReturn } from "@circle-react/contexts/Paywalls/usePaywallCheckout";
import type { CouponApplications } from "@circle-react/types";

export const CouponApplicationStatus: Record<string, ReturnStatus> = {
  NO_COUPON_APPLIED: "no_coupon_applied",
  COUPON_NOT_AVAILABLE: "coupon_not_available",
  COUPON_APPLIED: "coupon_applied",
};

type ReturnStatus =
  | "no_coupon_applied"
  | "coupon_not_available"
  | "coupon_applied";

interface CouponApplication {
  application_count: number | "Infinity";
  applications: CouponApplications["applications"];
  has_more: boolean;
  status: ReturnStatus;
}

export const findPrice = (
  paywallPriceId: number,
  prices?: Array<PaywallPrice>,
): PaywallPrice | undefined =>
  (prices || []).find((price: PaywallPrice) => price.id === paywallPriceId);

export const getCouponApplication = (
  couponApplication?: CouponApplications,
): CouponApplication => {
  if (!couponApplication) {
    return {
      status: CouponApplicationStatus.COUPON_NOT_AVAILABLE,
      applications: [],
      has_more: false,
      application_count: 0,
    };
  }

  if (
    couponApplication.application_count === "Infinity" ||
    (couponApplication.application_count || 0) > 0
  ) {
    return {
      status: CouponApplicationStatus.COUPON_APPLIED,
      applications: couponApplication.applications,
      has_more: couponApplication.has_more,
      application_count: couponApplication.application_count,
    };
  }

  return {
    status: CouponApplicationStatus.COUPON_NOT_AVAILABLE,
    applications: [],
    has_more: false,
    application_count: 0,
  };
};

export const useGetCouponApplication = (
  paywallPriceId: number,
  couponPreviewMutation: UsePaywallCheckoutReturn["couponPreviewMutation"],
): CouponApplication => {
  const data = couponPreviewMutation.data;
  const paywall = data?.paywall;
  const prices = paywall?.prices;

  return useMemo(() => {
    if (!couponPreviewMutation?.data || couponPreviewMutation.isError) {
      return {
        status: CouponApplicationStatus.NO_COUPON_APPLIED,
        applications: [],
        has_more: false,
        application_count: 0,
      };
    }

    const price = findPrice(paywallPriceId, prices);
    return getCouponApplication(price?.coupon_applications);
  }, [
    paywallPriceId,
    couponPreviewMutation?.data,
    prices,
    couponPreviewMutation.isError,
  ]);
};
